import { inject, Injectable } from '@angular/core';
import { environment } from '@env';
import {
  FinancialAccountActivities,
  FinancialAccountActivitiesRaw,
  FinancialAccountJournalActivities,
  FinancialAccountJournalActivitiesRaw,
  FinancialAccountJournalActivityItem,
  RequestPageParams,
  TransactionAdjustmentRequest,
  TransactionDetails,
  TransactionDetailsRaw,
  TransactionHistoryList,
  TransactionHistoryListResponse,
  TransactionHistoryRequest,
  TransactionList,
  TransactionListRaw,
  TransactionListRequest,
  TransactionStatusUpdateParams,
} from '@shared/models';
import { catchError, map, Observable, throwError } from 'rxjs';
import { BackendService } from '@shared/services/backend/backend.service';
import {
  mapTransactionDetails,
  mapTransactionHistoryData,
  mapTransactionListData,
  transactionsRequestParams,
} from './transaction-mapping-utils';
import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  financialAccountActivitiesRequestParams,
  mapFinancialAccountActivities,
} from '@shared/services/financial-account/financial-account-mapping-utils';
import { v4 as uuidv4 } from 'uuid';
import { IS_EXTENDED_ERRORS } from '@shared/interceptors';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { constants } from '@shared/constants';
@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  backendService = inject(BackendService);

  public getTransactions(requestParams: TransactionListRequest): Observable<TransactionList> {
    const params = transactionsRequestParams(requestParams);

    const headers = new HttpHeaders().set('KOR-Context', 'OPS');

    return this.backendService.get<TransactionListRaw>(`${environment.transactionsService}`, { params, headers }).pipe(
      map((transactionsData) => mapTransactionListData(transactionsData)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getTransactionById(transactionId: string): Observable<TransactionDetails> {
    return this.backendService.get<TransactionDetailsRaw>(`${environment.transactionsService}/${transactionId}`).pipe(
      map((transactionResponse) => mapTransactionDetails(transactionResponse)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getJournalActivity(requestParams: RequestPageParams, transactionId?: string): Observable<FinancialAccountJournalActivities> {
    const params = financialAccountActivitiesRequestParams(requestParams);
    return this.backendService
      .get<FinancialAccountJournalActivitiesRaw>(`${environment.financialAccountService}/journals?transactionId=${transactionId}`, {
        params,
      })
      .pipe(
        map((response) => {
          const { content = [], totalElements = 0 } = response ?? {};
          return { items: content, totalElements };
        }),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  updateTransactionStatus(transactionId: string, params: TransactionStatusUpdateParams): Observable<TransactionDetailsRaw> {
    const headers = new HttpHeaders().set('Idempotency-Key', uuidv4());

    return this.backendService.post<TransactionDetailsRaw>(`${environment.transactionsService}/${transactionId}/status`, params, {
      headers,
    });
  }

  createAdjustmentTransaction(params: TransactionAdjustmentRequest): Observable<FinancialAccountJournalActivityItem> {
    const headers = new HttpHeaders().set('Idempotency-Key', uuidv4());
    return this.backendService.post<FinancialAccountJournalActivityItem>(`${environment.transactionsService}/adjustment`, params, {
      headers,
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  /**
   * Returns Transaction History List
   * API /v1/transaction-histories
   *
   * @param page - page number
   * @param size - page size
   * @param transactionId - transaction id
   * @returns {Observable<TransactionHistoryList>}
   */

  public getTransactionHistory({ transactionId, page, size }: TransactionHistoryRequest): Observable<TransactionHistoryList> {
    const pageSize = size ?? constants.TABLE_ROWS;

    const params = new HttpParams({ encoder: new CustomHttpParamEncoder() })

      .set('transactionId', transactionId)
      .set('page', `${page ?? 0}`)
      .set('pageSize', `${pageSize}`);

    return this.backendService
      .get<TransactionHistoryListResponse>('/v1/transaction-histories', { params })
      .pipe(map((transactionHistoryResponse) => mapTransactionHistoryData(transactionHistoryResponse)));
  }

  getLedgerEntries(requestParams: RequestPageParams): Observable<FinancialAccountActivities> {
    const params = financialAccountActivitiesRequestParams(requestParams);

    return this.backendService.get<FinancialAccountActivitiesRaw>(`${environment.financialAccountService}/activities`, { params }).pipe(
      map((response) => mapFinancialAccountActivities(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }
}
